import { formatMoney, formatRate } from '@tumin/domain'
import { LOCALE } from '@/lib/constants'
import { EMPTY_VALUE } from '@/lib/dates'

/**
 * Tipos de valor numérico que muestra la app. Los montos llegan SIEMPRE en
 * centavos y las tasas en puntos básicos; aquí solo se formatean.
 */
export type ValueFormat = 'money' | 'moneyCompact' | 'number' | 'basisPoints' | 'days'

const integerFormatter = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 })
const compactFormatter = new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 })
const CENTS_PER_QUETZAL = 100

/** Formatea un valor según su tipo; `null`/`undefined` se muestran como "—". */
export function formatValue(value: number | null | undefined, format: ValueFormat): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE
  switch (format) {
    case 'money':
      return formatMoney(value)
    case 'moneyCompact':
      return `Q ${compactFormatter.format(value / CENTS_PER_QUETZAL)}`
    case 'number':
      return integerFormatter.format(value)
    case 'basisPoints':
      return formatRate(value, 1)
    case 'days':
      return `${integerFormatter.format(value)} ${value === 1 ? 'día' : 'días'}`
  }
}

/** Número entero con separador de miles. */
export function formatCount(value: number | null | undefined): string {
  return formatValue(value, 'number')
}

/**
 * Cantidad con su sustantivo en singular o plural: `countLabel(1, 'registro',
 * 'registros')` → "1 registro"; `countLabel(3, …)` → "3 registros". Evita el
 * "registro(s)" que confunde a quien lee despacio.
 */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${integerFormatter.format(count)} ${count === 1 ? singular : plural}`
}
